import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, FormRow } from './ProcurementShared';

const sectionStyle = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 20 };
const sectionTitle = { fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 14 };
const checkboxRow = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13 };

export default function ProcurementSettings() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [requiredFieldsText, setRequiredFieldsText] = useState('[]');
  const [attachmentScopeText, setAttachmentScopeText] = useState('[]');
  const [returnReasonsText, setReturnReasonsText] = useState('[]');
  const [matchToleranceText, setMatchToleranceText] = useState('{}');

  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState({ applies_to: 'po', name: '', code: '' });

  const fetchSettings = useCallback(async () => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/settings`, { headers: getAuthHeaders() });
    const data = await res.json();
    setSettings(data);
    setRequiredFieldsText(JSON.stringify(data.supplier_required_fields || [], null, 2));
    setAttachmentScopeText(JSON.stringify(data.attachment_scope || [], null, 2));
    setReturnReasonsText(JSON.stringify(data.return_reasons || [], null, 2));
    setMatchToleranceText(JSON.stringify(data.match_tolerance || {}, null, 2));
  }, [companyId]);

  const fetchStatuses = useCallback(async () => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/statuses`, { headers: getAuthHeaders() });
    setStatuses(await res.json());
  }, [companyId]);

  useEffect(() => { fetchSettings(); fetchStatuses(); }, [fetchSettings, fetchStatuses]);

  const update = (field, value) => setSettings({ ...settings, [field]: value });

  const save = async () => {
    let supplier_required_fields, attachment_scope, return_reasons, match_tolerance;
    try {
      supplier_required_fields = JSON.parse(requiredFieldsText);
      attachment_scope = JSON.parse(attachmentScopeText);
      return_reasons = JSON.parse(returnReasonsText);
      match_tolerance = JSON.parse(matchToleranceText);
    } catch (e) { alert('One of the JSON fields is invalid: ' + e.message); return; }

    setSaving(true);
    const payload = { ...settings, supplier_required_fields, attachment_scope, return_reasons, match_tolerance };
    delete payload.id; delete payload.company_id; delete payload.created_at; delete payload.updated_at;
    const res = await fetch(`${API}/companies/${companyId}/procurement/settings`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    setSaving(false);
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); return; }
    fetchSettings();
    alert('Settings saved');
  };

  const addStatus = async () => {
    if (!newStatus.name || !newStatus.code) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/statuses`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(newStatus),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Failed to add status'); return; }
    setNewStatus({ applies_to: 'po', name: '', code: '' });
    fetchStatuses();
  };

  const toggleStatusActive = async (s) => {
    await fetch(`${API}/companies/${companyId}/procurement/statuses/${s.id}`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ is_active: !s.is_active }),
    });
    fetchStatuses();
  };

  if (!settings) return <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>;

  const numberingTriplet = (label, prefixKey, nextKey, lenKey) => (
    <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
      <div style={{ flex: 2 }}><FormRow label={`${label} Prefix`}><input style={inputStyle} value={settings[prefixKey] || ''} onChange={e => update(prefixKey, e.target.value)} /></FormRow></div>
      <div style={{ flex: 1 }}><FormRow label="Next #"><input type="number" style={inputStyle} value={settings[nextKey] || 1} onChange={e => update(nextKey, Number(e.target.value))} /></FormRow></div>
      <div style={{ flex: 1 }}><FormRow label="Digits"><input type="number" style={inputStyle} value={settings[lenKey] || 4} onChange={e => update(lenKey, Number(e.target.value))} /></FormRow></div>
    </div>
  );

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>⚙️ Procurement Settings</h2>
        <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitle}>Document Numbering</div>
        {numberingTriplet('Purchase Request', 'pr_prefix', 'pr_next_number', 'pr_number_length')}
        {numberingTriplet('Purchase Order', 'po_prefix', 'po_next_number', 'po_number_length')}
        {numberingTriplet('Receiving', 'receiving_prefix', 'receiving_next_number', 'receiving_number_length')}
        {numberingTriplet('Return', 'return_prefix', 'return_next_number', 'return_number_length')}
        {numberingTriplet('Supplier', 'supplier_prefix', 'supplier_next_number', 'supplier_number_length')}
        {numberingTriplet('Supplier Invoice', 'supplier_invoice_prefix', 'supplier_invoice_next_number', 'supplier_invoice_number_length')}
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitle}>Purchase Requests & Orders</div>
        <FormRow label="Purchase Request Requirement">
          <select style={inputStyle} value={settings.pr_requirement || 'not_required'} onChange={e => update('pr_requirement', e.target.value)}>
            <option value="always_required">Always Required</option>
            <option value="permission_based">Permission-Based</option>
            <option value="not_required">Not Required</option>
          </select>
        </FormRow>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.allow_direct_po} onChange={e => update('allow_direct_po', e.target.checked)} /> Allow Direct Purchase Orders (without a PR)</label>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.multi_level_approval_enabled} onChange={e => update('multi_level_approval_enabled', e.target.checked)} /> Multi-Level Approval Enabled</label>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitle}>Suppliers</div>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.quick_supplier_creation_enabled} onChange={e => update('quick_supplier_creation_enabled', e.target.checked)} /> Quick Supplier Creation Enabled</label>
        <FormRow label="Supplier Confirmation Rule">
          <select style={inputStyle} value={settings.supplier_confirmation_rule || 'allow_incomplete'} onChange={e => update('supplier_confirmation_rule', e.target.value)}>
            <option value="allow_incomplete">Allow Incomplete</option>
            <option value="allow_with_warning">Allow With Warning</option>
            <option value="block_until_complete">Block Until Complete</option>
          </select>
        </FormRow>
        <FormRow label="Supplier Required Fields (JSON array)">
          <textarea style={{ ...inputStyle, minHeight: 70, fontFamily: 'monospace' }} value={requiredFieldsText} onChange={e => setRequiredFieldsText(e.target.value)} />
        </FormRow>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitle}>Attachments</div>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.attachments_enabled} onChange={e => update('attachments_enabled', e.target.checked)} /> Attachments Enabled</label>
        <FormRow label="Attachment Scope (JSON array)">
          <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'monospace' }} value={attachmentScopeText} onChange={e => setAttachmentScopeText(e.target.value)} />
        </FormRow>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitle}>Receiving & Returns</div>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.partial_receiving_enabled} onChange={e => update('partial_receiving_enabled', e.target.checked)} /> Partial Receiving Enabled</label>
        <FormRow label="Over-Receiving Rule">
          <select style={inputStyle} value={settings.over_receiving_rule || 'block'} onChange={e => update('over_receiving_rule', e.target.value)}>
            <option value="block">Block</option>
            <option value="allow_override">Allow Override</option>
          </select>
        </FormRow>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.returns_enabled} onChange={e => update('returns_enabled', e.target.checked)} /> Supplier Returns Enabled</label>
        <FormRow label="Return Reasons (JSON array)">
          <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'monospace' }} value={returnReasonsText} onChange={e => setReturnReasonsText(e.target.value)} />
        </FormRow>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitle}>Supplier Invoices & Payables</div>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.supplier_invoices_enabled} onChange={e => update('supplier_invoices_enabled', e.target.checked)} /> Supplier Invoices Enabled</label>
        <FormRow label="Matching Mode">
          <select style={inputStyle} value={settings.supplier_invoice_matching || 'none'} onChange={e => update('supplier_invoice_matching', e.target.value)}>
            <option value="none">None</option>
            <option value="po_match">Match Against PO</option>
            <option value="po_receiving_match">Match Against PO + Receiving</option>
          </select>
        </FormRow>
        <FormRow label="Payable Trigger">
          <select style={inputStyle} value={settings.payable_trigger || 'po_confirmation'} onChange={e => update('payable_trigger', e.target.value)}>
            <option value="po_confirmation">PO Confirmation</option>
            <option value="supplier_invoice_registration">Supplier Invoice Registration</option>
          </select>
        </FormRow>
        <FormRow label="Match Tolerance (JSON object, e.g. {&quot;qty_pct&quot;:5,&quot;cost_pct&quot;:2})">
          <textarea style={{ ...inputStyle, minHeight: 60, fontFamily: 'monospace' }} value={matchToleranceText} onChange={e => setMatchToleranceText(e.target.value)} />
        </FormRow>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitle}>Notifications</div>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.notify_pr_submitted} onChange={e => update('notify_pr_submitted', e.target.checked)} /> Notify when a PR is submitted</label>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.notify_po_approval_required} onChange={e => update('notify_po_approval_required', e.target.checked)} /> Notify when PO approval is required</label>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.notify_supplier_incomplete} onChange={e => update('notify_supplier_incomplete', e.target.checked)} /> Notify on incomplete supplier profiles</label>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.notify_delivery_due} onChange={e => update('notify_delivery_due', e.target.checked)} /> Notify when delivery is due soon</label>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.notify_delivery_overdue} onChange={e => update('notify_delivery_overdue', e.target.checked)} /> Notify when delivery is overdue</label>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.notify_receiving_partial} onChange={e => update('notify_receiving_partial', e.target.checked)} /> Notify on partial receiving</label>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.notify_supplier_invoice_difference} onChange={e => update('notify_supplier_invoice_difference', e.target.checked)} /> Notify on supplier invoice matching differences</label>
        <label style={checkboxRow}><input type="checkbox" checked={!!settings.notify_payable_due} onChange={e => update('notify_payable_due', e.target.checked)} /> Notify when a payable is due</label>
        <FormRow label="Delivery Due-Soon Threshold (days)">
          <input type="number" style={{ ...inputStyle, width: 120 }} value={settings.delivery_due_soon_days || 3} onChange={e => update('delivery_due_soon_days', Number(e.target.value))} />
        </FormRow>
      </div>

      <div style={sectionStyle}>
        <div style={sectionTitle}>Company-Created Statuses</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <select style={{ ...inputStyle, width: 100 }} value={newStatus.applies_to} onChange={e => setNewStatus({ ...newStatus, applies_to: e.target.value })}>
            <option value="po">PO</option><option value="pr">PR</option>
          </select>
          <input style={inputStyle} placeholder="Name" value={newStatus.name} onChange={e => setNewStatus({ ...newStatus, name: e.target.value })} />
          <input style={inputStyle} placeholder="Code" value={newStatus.code} onChange={e => setNewStatus({ ...newStatus, code: e.target.value })} />
          <button style={btnSecondary} onClick={addStatus}>+ Add</button>
        </div>
        <table style={tableStyle}>
          <thead><tr>{['Applies To', 'Name', 'Code', 'Active', ''].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {statuses.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No custom statuses</td></tr>}
            {statuses.map(s => (
              <tr key={s.id}>
                <td style={tdStyle}>{s.applies_to.toUpperCase()}</td>
                <td style={tdStyle}>{s.name}</td>
                <td style={tdStyle}>{s.code}</td>
                <td style={tdStyle}><Badge value={s.is_active ? 'active' : 'inactive'} /></td>
                <td style={tdStyle}><button style={btnSmall} onClick={() => toggleStatusActive(s)}>{s.is_active ? 'Deactivate' : 'Activate'}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
